/**
 * Sheet con el historial de movimientos de stock de un producto/insumo.
 * Se usa en Petshop (catálogo), Farmacia e Insumos.
 */
import { Text, View } from 'react-native';
import { ArrowDownRight, ArrowUpRight, ClipboardCheck, PackagePlus, RotateCcw, Sparkles } from 'lucide-react-native';

import { Sheet, SkeletonRows } from '@/components/ui';
import { useFirestoreCollection } from '@/hooks/useFirestoreCollection';
import { movimientosDe, type MovimientoStock, type TipoMovimiento } from '@/services/firebase/movimientosStock';
import { colors, typography } from '@/theme/tokens';
import { formatDateTime } from '@/utils/format';

const META: Record<TipoMovimiento, { label: string; color: string; Icon: any }> = {
  inicial: { label: 'Creación', color: colors.ink[500], Icon: Sparkles },
  ingreso: { label: 'Reposición', color: colors.success[600], Icon: PackagePlus },
  ajuste: { label: 'Ajuste', color: colors.warning[600], Icon: PackagePlus },
  venta: { label: 'Venta', color: colors.danger[600], Icon: ArrowDownRight },
  uso: { label: 'Uso', color: colors.danger[600], Icon: ArrowDownRight },
  salida: { label: 'Salida', color: colors.danger[600], Icon: ArrowDownRight },
  entrega: { label: 'Entrega (solicitud)', color: colors.danger[600], Icon: ClipboardCheck },
  anulacion: { label: 'Anulación', color: colors.success[600], Icon: RotateCcw },
};

export function HistorialStockSheet({
  productoId,
  productoNombre,
  visible,
  onClose,
}: {
  productoId: string | null;
  productoNombre: string;
  visible: boolean;
  onClose: () => void;
}) {
  const q = productoId ? movimientosDe(productoId) : null;
  const { data, loading } = useFirestoreCollection<MovimientoStock>(q);

  return (
    <Sheet visible={visible} onClose={onClose} size="md" title="Historial de stock" subtitle={productoNombre}>
      {loading ? (
        <SkeletonRows rows={4} />
      ) : data.length === 0 ? (
        <View style={{ paddingVertical: 24 }}>
          <Text style={{ fontSize: 13, color: colors.ink[500], textAlign: 'center' }}>
            Todavía no hay movimientos registrados de este producto.
          </Text>
        </View>
      ) : (
        <View style={{ gap: 0 }}>
          {data.map((m, idx) => {
            const meta = META[m.tipo] ?? META.ajuste;
            const Icon = meta.Icon;
            const positivo = m.delta >= 0;
            return (
              <View
                key={m.id}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 12,
                  paddingVertical: 11,
                  borderBottomWidth: idx === data.length - 1 ? 0 : 1,
                  borderBottomColor: colors.ink[100],
                }}
              >
                <View style={{ width: 34, height: 34, borderRadius: 10, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.crema[100] }}>
                  <Icon size={17} color={meta.color} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 14, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
                    {meta.label}
                    {m.motivo ? <Text style={{ fontWeight: typography.weights.regular, color: colors.ink[500] }}>{`  ·  ${m.motivo}`}</Text> : null}
                  </Text>
                  <Text style={{ fontSize: 11, color: colors.ink[400], marginTop: 1 }}>
                    {formatDateTime(m.creadoEn?.toDate?.() ?? null)}
                    {m.autorNombre ? ` · ${m.autorNombre}` : ''}
                  </Text>
                </View>
                <View style={{ alignItems: 'flex-end' }}>
                  <Text style={{ fontSize: 15, fontWeight: typography.weights.extrabold, color: positivo ? colors.success[600] : colors.danger[600] }}>
                    {positivo ? '+' : ''}{m.delta}
                  </Text>
                  {m.stockResultante != null ? (
                    <Text style={{ fontSize: 11, color: colors.ink[400] }}>quedó {m.stockResultante}</Text>
                  ) : null}
                </View>
              </View>
            );
          })}
        </View>
      )}
    </Sheet>
  );
}
